/**
 * Multi-iframe ownership handshake for the unified embed script.
 *
 * The unified script is injected into EVERY frame of the host page (the guide needs to reach
 * elements inside nested iframes). The floating bot widget + its browser client, however, must
 * live in exactly ONE frame: a second copy booted inside a nested iframe registers a stray
 * browser-client bridge that steals the real widget's `session_ready`, so the backend's
 * waitForClient() never resolves and every API dispatch hangs on "Pensando…". So a nested frame
 * boots the bot only when NO ancestor frame is already running getdemo — the same
 * CHECK_IF_PARENT_HAS_SCRIPT / PARENT_HAS_SCRIPT handshake the guide uses
 * (GuideManager.startIframe). The GUIDE keeps running in every frame (it self-handles the iframe
 * case); only the BOT is gated on {@link ownsFloatingWidget}.
 *
 * Extracted from index.ts (which runs `main()` on import) so the handshake is unit-testable.
 */
import { MessageTypes } from '../user-guide/types/MessageTypes';

/** How long a nested frame waits for a PARENT_HAS_SCRIPT reply before claiming ownership. */
export const OWNERSHIP_PROBE_TIMEOUT_MS = 1000;

/**
 * Answer descendant frames' ownership probes. Registered in every getdemo frame so ownership
 * resolves to the TOPMOST getdemo frame and chains downward. Works even when only the copilot
 * (not the guide) is enabled — the guide's own responder only exists when the guide is started.
 */
export const answerOwnershipProbes = (): void => {
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === MessageTypes.CHECK_IF_PARENT_HAS_SCRIPT) {
      (event.source as Window | null)?.postMessage({ type: MessageTypes.PARENT_HAS_SCRIPT }, '*');
    }
  });
};

/**
 * Whether THIS frame should own the floating bot widget. The top frame always owns it; a nested
 * frame owns it only if its parent chain has no getdemo script (no PARENT_HAS_SCRIPT reply within
 * the timeout). Mirrors GuideManager.startIframe's handshake.
 */
export const ownsFloatingWidget = async (): Promise<boolean> => {
  if (window.top === window.self) return true;
  return new Promise<boolean>(resolve => {
    let settled = false;
    const finish = (owns: boolean): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onReply);
      resolve(owns);
    };
    const onReply = (event: MessageEvent): void => {
      if (event.data?.type === MessageTypes.PARENT_HAS_SCRIPT) finish(false);
    };
    window.addEventListener('message', onReply);
    try {
      window.parent.postMessage({ type: MessageTypes.CHECK_IF_PARENT_HAS_SCRIPT }, '*');
    } catch {
      /* parent unreachable — decide by timeout */
    }
    window.setTimeout(() => finish(true), OWNERSHIP_PROBE_TIMEOUT_MS);
  });
};
